import { inject, Injectable } from '@angular/core';
import { injectQuery, injectMutation, QueryClient } from '@tanstack/angular-query-experimental';
import { InvoiceService } from '@core/services/invoice.service';
import { PaginationConfig, PaginationResult } from '@core/services/unit.service';
import { ApiResponse } from '@core/types';
import {
  Invoice,
  InvoiceDetail,
  InvoiceCreate,
  InvoiceUpdate,
  InvoiceBatchCreate,
  InvoiceGapMonth,
  InvoiceBatchByMonth,
  InvoiceBatchByMonthResult,
} from '@core/types/finance.type';
import { QUERY_KEYS } from '@core/constans';
import { environment } from '@environments/environment';
import { lastValueFrom } from 'rxjs';
import { profileStore } from '@core/store';

@Injectable({ providedIn: 'root' })
export class InvoiceQuery {
  private readonly invoiceService = inject(InvoiceService);
  private readonly queryClient = inject(QueryClient);
  private readonly profileStore = inject(profileStore);

  useInvoicesGrid(config: () => PaginationConfig) {
    return injectQuery(() => {
      const condominiumId = this.profileStore.data.activeCondominiumId();
      const currentConfig = config();
      return {
        queryKey: [...QUERY_KEYS.invoices.grid(currentConfig), condominiumId],
        queryFn: () => lastValueFrom(this.invoiceService.getInvoices(currentConfig)),
        select: (data: ApiResponse<PaginationResult<Invoice>>) => data.data,
        staleTime: environment.staleTime,
        enabled: !!condominiumId,
      };
    });
  }

  useInvoiceDetail(invoiceId: () => string | null) {
    return injectQuery(() => {
      const id = invoiceId();
      return {
        queryKey: QUERY_KEYS.invoices.detail(id || ''),
        queryFn: () => lastValueFrom(this.invoiceService.getInvoice(id!)),
        select: (data: ApiResponse<InvoiceDetail>) => data.data,
        enabled: !!id,
      };
    });
  }

  useCreateInvoice() {
    return injectMutation(() => ({
      mutationFn: (data: InvoiceCreate) =>
        lastValueFrom(this.invoiceService.createInvoice(data)),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.invoices.all });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.finance.dashboard() });
      },
    }));
  }

  useCreateInvoiceBatch() {
    return injectMutation(() => ({
      mutationFn: (data: InvoiceBatchCreate) =>
        lastValueFrom(this.invoiceService.createInvoiceBatch(data)),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.invoices.all });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.finance.dashboard() });
      },
    }));
  }

  useInvoiceGaps() {
    return injectQuery(() => {
      const condominiumId = this.profileStore.data.activeCondominiumId();
      return {
        queryKey: [...QUERY_KEYS.invoices.gaps(), condominiumId],
        queryFn: () => lastValueFrom(this.invoiceService.getInvoiceGaps()),
        select: (data: ApiResponse<InvoiceGapMonth[]>) => data.data ?? [],
        staleTime: environment.staleTime,
        enabled: !!condominiumId,
      };
    });
  }

  useCreateInvoiceBatchByMonth() {
    return injectMutation(() => ({
      mutationFn: (data: InvoiceBatchByMonth) =>
        lastValueFrom(this.invoiceService.createInvoiceBatchByMonth(data)),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.invoices.all });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.invoices.gaps() });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.finance.dashboard() });
      },
    }));
  }

  useUpdateInvoice() {
    return injectMutation(() => ({
      mutationFn: (data: InvoiceUpdate) =>
        lastValueFrom(this.invoiceService.updateInvoice(data)),
      onSuccess: (_result, variables) => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.invoices.detail(variables.id) });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.invoices.all });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.finance.dashboard() });
      },
    }));
  }

  useDeleteInvoice() {
    return injectMutation(() => ({
      mutationFn: (id: string) => lastValueFrom(this.invoiceService.deleteInvoice(id)),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.invoices.all });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.finance.dashboard() });
      },
    }));
  }

  useCancelInvoice() {
    return injectMutation(() => ({
      mutationFn: (id: string) => lastValueFrom(this.invoiceService.cancelInvoice(id)),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.invoices.all });
        this.queryClient.invalidateQueries({ queryKey: QUERY_KEYS.finance.dashboard() });
      },
    }));
  }

  useSendInvoiceToResident() {
    return injectMutation(() => ({
      mutationFn: (id: string) =>
        lastValueFrom(this.invoiceService.sendInvoiceToResident(id)),
    }));
  }
}
